import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Ruler, ChevronRight, GitBranch, Filter, X } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { projectsApi } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { StatusBadge } from '../components/StatusBadge';
import { toast } from 'sonner';

// Full lifecycle: every stage from Under Contract (Buy) to Sold/Note Held
const PIPELINE_STAGES = [
  { key: 'Under_Contract', label: 'Under Contract (Buy)', section: 'active' },
  { key: 'Owned', label: 'Owned', section: 'active' },
  { key: 'Subdividing', label: 'Subdividing', section: 'active' },
  { key: 'Listed', label: 'Listed', section: 'active' },
  { key: 'Under_Contract_Sell', label: 'Under Contract (Sell)', section: 'active' },
  { key: 'Sold', label: 'Sold', section: 'completed' },
  { key: 'Note_Held', label: 'Note Held', section: 'completed' },
  { key: 'Dead', label: 'Dead Deal', section: 'dead' },
];

const SECTION_CONFIG = {
  active: { title: 'Active Pipeline', color: 'text-foreground' },
  completed: { title: 'Completed', color: 'text-emerald-700' },
  dead: { title: 'Dead Deals', color: 'text-red-600' },
};

const STRATEGY_OPTIONS = [
  { value: 'all', label: 'All Strategies' },
  { value: 'Flip', label: 'Flip' },
  { value: 'Subdivide', label: 'Subdivide' },
];

// Child lot card
const ChildLotCard = ({ project }) => {
  const statusLabel = {
    Under_Contract: 'Under Contract (Buy)',
    Owned: 'Owned',
    Subdividing: 'Subdividing',
    Listed: 'Listed',
    Under_Contract_Sell: 'Under Contract (Sell)',
    Sold: 'Sold',
    Note_Held: 'Note Held',
    Dead: 'Dead',
  }[project.status] || project.status;

  const statusStyle = {
    Listed: 'bg-cyan-50 text-cyan-700 border-cyan-200',
    Under_Contract_Sell: 'bg-teal-50 text-teal-700 border-teal-200',
    Sold: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    Note_Held: 'bg-orange-50 text-orange-700 border-orange-200',
    Under_Contract: 'bg-amber-50 text-amber-700 border-amber-200',
    Owned: 'bg-green-50 text-green-700 border-green-200',
  }[project.status] || 'bg-gray-50 text-gray-600 border-gray-200';

  return (
    <Link
      to={`/projects/${project.id}`}
      className="block ml-4 pl-3 py-2 border-l-2 border-purple-200 hover:border-purple-400 hover:bg-purple-50/50 transition-all"
      data-testid={`child-lot-${project.id}`}
    >
      <div className="flex items-center gap-2">
        <ChevronRight className="w-3 h-3 text-purple-400 flex-shrink-0" />
        <span className="text-sm font-medium text-purple-800 truncate">{project.name}</span>
        <Badge variant="outline" className={`text-[9px] rounded-sm whitespace-nowrap flex-shrink-0 ${statusStyle}`}>
          {statusLabel}
        </Badge>
      </div>
      <div className="flex items-center gap-3 mt-1 ml-5 text-xs text-muted-foreground">
        {project.cost_basis_percentage != null && (
          <span>{project.cost_basis_percentage}% allocation</span>
        )}
        {project.sale_price > 0 && (
          <span className="font-mono text-emerald-600">{formatCurrency(project.sale_price)}</span>
        )}
      </div>
    </Link>
  );
};

// Project card with nested children
const ProjectCard = ({ project, childProjects = [] }) => {
  const hasChildren = childProjects.length > 0;

  return (
    <div className="pipeline-card" data-testid={`project-card-${project.id}`}>
      <Link
        to={`/projects/${project.id}`}
        className="block hover:bg-muted/30 -m-3 p-3 rounded-sm transition-colors"
      >
        <div className="font-semibold text-sm mb-1 truncate">{project.name}</div>
        <div className="text-xs text-muted-foreground space-y-1">
          {project.county && project.state && (
            <div className="flex items-center gap-1">
              <MapPin className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">{project.county}, {project.state}</span>
            </div>
          )}
          {project.acres > 0 && (
            <div className="flex items-center gap-1">
              <Ruler className="w-3 h-3 flex-shrink-0" />
              <span>{Number(project.acres).toFixed(2)} acres</span>
            </div>
          )}
        </div>
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          {project.strategy && (
            <Badge
              variant="outline"
              className={`text-[10px] rounded-sm ${project.strategy === 'Flip' ? 'badge-flip' : 'badge-subdivide'}`}
            >
              {project.strategy}
            </Badge>
          )}
          {project.purchase_price > 0 && (
            <span className="text-xs text-muted-foreground font-mono">
              {formatCurrency(project.purchase_price)}
            </span>
          )}
        </div>
      </Link>

      {hasChildren && (
        <div className="mt-3 pt-2 border-t border-dashed border-purple-200">
          <div className="flex items-center gap-1 mb-2 text-xs text-purple-600">
            <GitBranch className="w-3 h-3" />
            <span className="font-medium">{childProjects.length} Child Lots</span>
          </div>
          <div className="space-y-1">
            {childProjects.map((child) => (
              <ChildLotCard key={child.id} project={child} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Kanban column
const KanbanColumn = ({ stage, projects, childrenMap }) => {
  // Only show top-level projects (not children) in the column
  const topLevelProjects = projects.filter(p => !p.parent_project_id);

  // Count nested children displayed under parents in THIS column
  const nestedChildCount = topLevelProjects.reduce(
    (sum, p) => sum + (childrenMap[p.id] || []).length, 0
  );

  return (
    <div className="kanban-column flex-shrink-0 w-[260px] sm:w-[280px]" data-testid={`kanban-column-${stage.key}`}>
      <div className="kanban-column-header">
        <div className="flex items-center gap-2 min-w-0">
          <StatusBadge status={stage.key} size="sm" />
        </div>
        <span className="kanban-column-count whitespace-nowrap">
          {topLevelProjects.length}
          {nestedChildCount > 0 && (
            <span className="text-purple-600 ml-1 text-xs">(+{nestedChildCount} lots)</span>
          )}
        </span>
      </div>
      <div className="kanban-cards space-y-3">
        {topLevelProjects.map((project) => (
          <ProjectCard
            key={project.id}
            project={project}
            childProjects={childrenMap[project.id] || []}
          />
        ))}
        {topLevelProjects.length === 0 && (
          <div className="text-center text-muted-foreground text-xs py-6 italic">
            No projects
          </div>
        )}
      </div>
    </div>
  );
};

// Filter pill
const FilterPill = ({ label, active, onClick, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`px-3 py-1 text-xs rounded-full border transition-colors whitespace-nowrap ${
      active
        ? 'bg-primary text-primary-foreground border-primary'
        : 'bg-background text-muted-foreground border-border hover:border-primary/40 hover:text-foreground'
    }`}
  >
    {label}
  </button>
);

export const Pipeline = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [strategyFilter, setStrategyFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      const response = await projectsApi.getAll();
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to load projects:', error);
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  // Build child lookup map: parentId -> [child projects]
  const childrenMap = useMemo(() => {
    const map = {};
    projects.forEach(p => {
      if (p.parent_project_id) {
        if (!map[p.parent_project_id]) map[p.parent_project_id] = [];
        map[p.parent_project_id].push(p);
      }
    });
    return map;
  }, [projects]);

  // Apply filters to projects
  const filteredProjects = useMemo(() => {
    return projects.filter(p => {
      // Strategy filter (only applies to top-level projects; children inherit)
      if (strategyFilter !== 'all') {
        if (p.parent_project_id) {
          // For child lots, check parent's strategy
          const parent = projects.find(pp => pp.id === p.parent_project_id);
          if (parent && parent.strategy !== strategyFilter) return false;
        } else if (p.strategy && p.strategy !== strategyFilter) {
          return false;
        }
      }
      // Status filter
      if (statusFilter !== 'all' && p.status !== statusFilter) return false;
      return true;
    });
  }, [projects, statusFilter, strategyFilter]);

  // Group filtered projects by status
  const projectsByStatus = useMemo(() => {
    const grouped = {};
    PIPELINE_STAGES.forEach(s => { grouped[s.key] = []; });
    filteredProjects.forEach(p => {
      if (grouped[p.status]) {
        grouped[p.status].push(p);
      }
    });
    return grouped;
  }, [filteredProjects]);

  // Determine which stages to show based on status filter
  const visibleStages = useMemo(() => {
    if (statusFilter === 'all') return PIPELINE_STAGES;
    return PIPELINE_STAGES.filter(s => s.key === statusFilter);
  }, [statusFilter]);

  // Group visible stages by section
  const sections = useMemo(() => {
    const result = {};
    visibleStages.forEach(stage => {
      if (!result[stage.section]) result[stage.section] = [];
      result[stage.section].push(stage);
    });
    return result;
  }, [visibleStages]);

  const hasActiveFilters = statusFilter !== 'all' || strategyFilter !== 'all';
  const totalCount = projects.filter(p => !p.parent_project_id).length;
  const filteredCount = filteredProjects.filter(p => !p.parent_project_id).length;

  const clearFilters = () => {
    setStatusFilter('all');
    setStrategyFilter('all');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="pipeline-loading">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="pipeline-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl sm:text-2xl font-heading font-bold">Pipeline</h1>
          <p className="text-xs sm:text-sm text-muted-foreground">
            {hasActiveFilters
              ? `Showing ${filteredCount} of ${totalCount} projects`
              : `${totalCount} projects across all stages`}
          </p>
        </div>
        <Button
          variant={showFilters ? 'default' : 'outline'}
          size="sm"
          onClick={() => setShowFilters(!showFilters)}
          data-testid="toggle-filters-btn"
          className="gap-1.5"
        >
          <Filter className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">Filters</span>
          {hasActiveFilters && (
            <span className="bg-primary-foreground text-primary text-[10px] rounded-full w-4 h-4 flex items-center justify-center">
              {(statusFilter !== 'all' ? 1 : 0) + (strategyFilter !== 'all' ? 1 : 0)}
            </span>
          )}
        </Button>
      </div>

      {/* Filter Bar */}
      {showFilters && (
        <div className="bg-muted/40 border border-border rounded-sm p-3 space-y-3 animate-slide-in" data-testid="filter-bar">
          {/* Status Filter */}
          <div>
            <div className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wide">Status</div>
            <div className="flex flex-wrap gap-1.5">
              <FilterPill
                label="All Stages"
                active={statusFilter === 'all'}
                onClick={() => setStatusFilter('all')}
                testId="filter-status-all"
              />
              {PIPELINE_STAGES.map(stage => (
                <FilterPill
                  key={stage.key}
                  label={stage.label}
                  active={statusFilter === stage.key}
                  onClick={() => setStatusFilter(statusFilter === stage.key ? 'all' : stage.key)}
                  testId={`filter-status-${stage.key}`}
                />
              ))}
            </div>
          </div>

          {/* Strategy Filter (Sub-Status) */}
          <div>
            <div className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wide">Strategy</div>
            <div className="flex flex-wrap gap-1.5">
              {STRATEGY_OPTIONS.map(opt => (
                <FilterPill
                  key={opt.value}
                  label={opt.label}
                  active={strategyFilter === opt.value}
                  onClick={() => setStrategyFilter(opt.value)}
                  testId={`filter-strategy-${opt.value}`}
                />
              ))}
            </div>
          </div>

          {/* Clear all */}
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
              data-testid="clear-filters-btn"
            >
              <X className="w-3 h-3" />
              Clear all filters
            </button>
          )}
        </div>
      )}

      {/* Pipeline Sections */}
      {Object.entries(sections).map(([sectionKey, stages]) => {
        const config = SECTION_CONFIG[sectionKey];
        const sectionProjectCount = stages.reduce(
          (sum, s) => sum + (projectsByStatus[s.key] || []).filter(p => !p.parent_project_id).length,
          0
        );

        // Hide dead section if no dead projects and no filter active
        if (sectionKey === 'dead' && sectionProjectCount === 0 && statusFilter !== 'Dead') {
          return null;
        }

        return (
          <div key={sectionKey} data-testid={`pipeline-section-${sectionKey}`}>
            <div className="flex items-center gap-2 mb-3">
              <h2 className={`text-sm font-semibold uppercase tracking-wide ${config.color}`}>
                {config.title}
              </h2>
              <span className="text-xs text-muted-foreground">({sectionProjectCount})</span>
            </div>
            <div
              className="flex gap-3 overflow-x-auto pb-4 -mx-3 px-3 sm:mx-0 sm:px-0 snap-x snap-mandatory"
              style={{ WebkitOverflowScrolling: 'touch' }}
              data-testid={`pipeline-scroll-${sectionKey}`}
            >
              {stages.map(stage => (
                <div key={stage.key} className="snap-start">
                  <KanbanColumn
                    stage={stage}
                    projects={projectsByStatus[stage.key] || []}
                    childrenMap={childrenMap}
                  />
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {/* Empty state when filters match nothing */}
      {filteredCount === 0 && hasActiveFilters && (
        <div className="text-center py-12" data-testid="pipeline-empty">
          <p className="text-muted-foreground text-sm">No projects match your filters.</p>
          <Button variant="link" size="sm" onClick={clearFilters} className="mt-2">
            Clear filters
          </Button>
        </div>
      )}
    </div>
  );
};

export default Pipeline;
